import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import UserManagement from './UserManagement';
import RoleManagement from './RoleManagement';
import LogViewer from './LogViewer';
import './AdminView.css';

const AdminView: React.FC = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<'users' | 'roles' | 'logs'>('users');
    const [isDirty, setIsDirty] = useState(false);

    useEffect(() => {
        if (!isDirty) return;
        
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = '';
        };

        const handleAnchorClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const anchor = target.closest('a');
            
            if (anchor && anchor.href && anchor.href.startsWith(window.location.origin)) {
                if (!window.confirm("저장하지 않은 설정이 있습니다. 다른 대메뉴로 이동하시겠습니까?\n이동 시 편집 중이던 권한 내용이 영구적으로 사라집니다.")) {
                    e.preventDefault();
                    e.stopPropagation();
                } else {
                    setIsDirty(false);
                }
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        document.addEventListener('click', handleAnchorClick, true);
        
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener('click', handleAnchorClick, true);
        };
    }, [isDirty]);

    const handleTabChange = (newTab: 'users' | 'roles' | 'logs') => {
        if (activeTab === newTab) return;
        if (isDirty) {
            if (!window.confirm("저장하지 않은 설정이 있습니다. 진짜 화면을 이동하시겠습니까?\n이동 시 편집 중이던 권한 내용이 영구적으로 사라집니다.")) {
                return;
            }
            setIsDirty(false);
        }
        setActiveTab(newTab);
    };

    return (
        <div className="admin-container">
            <div className="admin-sidebar">
                <div className="admin-sidebar-header">
                    <h3>관리자 설정</h3>
                </div>
                <nav className="admin-nav">
                    <button 
                        className={`admin-nav-item ${activeTab === 'users' ? 'active' : ''}`}
                        onClick={() => handleTabChange('users')}
                    >
                        사용자 계정 관리
                    </button>
                    <button 
                        className={`admin-nav-item ${activeTab === 'roles' ? 'active' : ''}`}
                        onClick={() => handleTabChange('roles')}
                    >
                        권한 그룹 설정
                    </button>
                    <button 
                        className={`admin-nav-item ${activeTab === 'logs' ? 'active' : ''}`}
                        onClick={() => handleTabChange('logs')}
                    >
                        사용자 활동 기록
                    </button>
                </nav>
            </div>
            <div className="admin-content">
                <div className="admin-content-header">
                    <h2>
                        {activeTab === 'users' && '사용자 계정 관리'}
                        {activeTab === 'roles' && '권한 그룹 설정'}
                        {activeTab === 'logs' && '사용자 활동 기록'}
                    </h2>
                    <p>
                        {activeTab === 'users' && '플랫폼에 접근할 수 있는 사용자 계정을 관리하고 권한 역할을 부여합니다.'}
                        {activeTab === 'roles' && '시스템 내 역할을 정의하고 각 메뉴별 접근 권한을 설정합니다.'}
                        {activeTab === 'logs' && '플랫폼 내 관리자들의 접속 이력과 데이터 수정, 내보내기 등 모든 활동을 추적합니다.'}
                    </p>
                </div>
                <div className="admin-main-card">
                    {activeTab === 'users' && <UserManagement />}
                    {activeTab === 'roles' && <RoleManagement onDirtyChange={setIsDirty} />}
                    {activeTab === 'logs' && <LogViewer />}
                </div>
            </div>
        </div>
    );
};

export default AdminView;
